import React, { useState, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { HiHeaderComponent, ActivitiesSection, ActivityTodayHeader } from '../components';
import { ScrollView, RefreshControl } from 'react-native';
import { View } from '@shared/components/md-design-system/Layout/View';
import { useFetch } from 'app/queryProvider';
import { IPathwayActivitiesResponse } from 'app/_modules/pathways/entities';
import { Loading } from '@shared/components/loading';
import { useAuth } from 'app/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import MainErrorWithIcon from '@shared/components/ModularInfoWithIcon';
import { IReportsResponse, TelemonitoringReportCard } from 'app/_modules/history';
import { Telemetry } from 'app/services/telemetry/Telemetry';
import { useWebSocketMeasurementService } from '@shared/hooks/useWebSocketMeasurementService';
import { useMergedActivities } from '@shared/hooks/useMergedActivities';

const Home = () => {
    const { meData } = useAuth()
    const { t } = useTranslation();
    const [refreshing, setRefreshing] = useState(false);
    const { measurements } = useWebSocketMeasurementService();

    const todayFormattedDate = new Intl.DateTimeFormat('en-CA').format(new Date());

    const pathwayId = meData?.current_pathway? meData?.current_pathway.id : undefined ;

    const { data: pathwayActivities, isFetching: isFetchingPathwayActivities, isError: isActivitiesError, refetch: refetchActivities } = useFetch<IPathwayActivitiesResponse>({
        url: `/activities?date=${todayFormattedDate}`,
        queryKey: [["pathway", "activities"]],
        resource: { name: "pathways", id: pathwayId },
        headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0'
        },
        options: {
            enabled: !!pathwayId
        }
    });

    const { data: reportsResponse, isFetching: isFetchingReports, refetch: refetchReports } = useFetch<IReportsResponse>({
        url: '?_filter={"status":"sent", "opened_by_patient":false}',
        queryKey: [["telemonitoring-reports", "list", "not_opened_by_patient"]],
        resource: { name: "telemonitoring-reports" },
        options: {
            enabled: !!pathwayId
        }
    });

    const mergedActivities = useMergedActivities(pathwayActivities, measurements);

    const hasActivities = !!mergedActivities && (
        (mergedActivities.daily?.length ?? 0) > 0 ||
        (mergedActivities.morning?.length ?? 0) > 0 ||
        (mergedActivities.afternoon?.length ?? 0) > 0 ||
        (mergedActivities.evening?.length ?? 0) > 0
    );

    const reports = reportsResponse?.results || [];

    useFocusEffect(
        useCallback(() => {
            if (pathwayId) {
                refetchActivities();
                refetchReports();
            }
        }, [pathwayId, refetchActivities, refetchReports])
    );

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        Telemetry.log('info', 'Refreshing home screen data...', { date: new Date().toLocaleString() });
        try {
            await Promise.all([
                refetchActivities(),
                refetchReports()
            ]);
        } finally {
            setRefreshing(false);
        }
    }, [refetchActivities, refetchReports]);


    if (isFetchingPathwayActivities || !pathwayId) {
        return <Loading message={t('commons.loading.pathway')} />
    }

    return (
        <ScrollView
            refreshControl={
                <RefreshControl
                    refreshing={refreshing}
                    onRefresh={onRefresh}
                />
            }
        >
            <HiHeaderComponent />
            <View variant="pagecontainer">
                <ActivityTodayHeader />
                {reports.map(report => (
                    <TelemonitoringReportCard
                        key={report.id}
                        openedByPatient={false}
                        reportData={{
                            id: report.id,
                            title: report.reportName,
                            startDate: report.periodStartDate,
                            endDate: report.periodEndDate,
                            receivedDate: report.createdAt,
                            status: report.status
                        }}
                    />
                ))}
                {isActivitiesError ? (
                    <MainErrorWithIcon errorType={'no_connection'} />
                ) : hasActivities ? (
                    <ActivitiesSection activities={mergedActivities!} />
                ) : (
                    <MainErrorWithIcon errorType={'no_activities'} />
                )}
            </View>
        </ScrollView>
    );
};
export default Home;
